// "Starfield" (night sky) recipe — the UNMAPPED-REGION material's BASE texture (2026-08-13).
// This is a DERIVED recipe (D10): it extends `starfield_full` and DISABLES the three ambience
// layers (nebula, milkyWay, clusters), leaving just the sky base + the pixel-locked star stack.
// The "Starfield (full)" recipe (`Recipe_StarfieldFull.ts`) keeps those extra layers.
//
// WHY SPLIT: the user found the nebula / milky-way / cluster features too distracting for the
// unmapped BASAL texture (they read as terrain details that don't exist), but still wanted the
// full version available as a separate preset.  So this plain "Starfield" is the calm night sky,
// and "Starfield (full)" adds the ambience for when a richer read is wanted.
//
// WHY IT EXISTS AT ALL (shared with the full recipe): this is a dream-mapping app, so large
// swathes of every map are UNMAPPED (never visited in a dream).  Those areas fall through to
// `Mod_TerrainMaterials.baseRecipeID` = splat SLOT 0 of every chunk, and the previous fallback
// (snow) was wrong twice over: real snow became indistinguishable from unmapped land, and snow
// reads as "polar", not as "no data".  A starfield reads unmistakably as "not surveyed" and is
// evocative of lucid dreaming.  (This recipe only REGISTERS itself — the default baseRecipeID
// stays "dirt"; the user switches it in the map UI.)
//
// THE ZOOM-INDEPENDENCE PROPERTY (the core design decision): a starfield must read as a starfield
// at EVERY zoom, from "the whole world" to "a single room".  A world-space star lattice cannot do
// that — at one zoom the stars are sub-pixel (and the AA band-limit correctly averages them into
// a flat grey wash), at another they are dinner plates.  So the star layers are PIXEL-LOCKED
// (`scaleSpace: 1` → the lattice spacing is in PIXELS, i.e. spacing * matPixelSize() metres),
// which keeps star size and star COUNT PER SCREEN constant at every zoom.  The cost is that a
// zoom change reshuffles which world points carry stars; the user has explicitly accepted this
// (the terrain re-solve takes ~2 s after a zoom change, which hides the reshuffle).  Panning at a
// FIXED zoom keeps the cell size constant, so stars stay world-anchored while you pan.
//
// NO TIME UNIFORM (Recipe_Water's constraint): procedural materials are deterministic per camera
// position + zoom, so there is no twinkle and no animation anywhere in here.
//
// LAYER STACK (what remains after the disables — see Recipe_StarfieldFull.ts for the full stack):
//   1. skyGhost   lightModulatedRamp — the base colour AND the "hills stay faintly visible" ask.
//                 The blend computes dot(matGeoNormal(), matLightDir()) per fragment and mixes the
//                 layer's own colour toward `shadowColor` by (1 - lit) * amount, so unmapped
//                 TERRAIN SHAPE ghosts through as a soft darkening of the sky — vague outlines of
//                 the surrounding land with no surface detail, exactly the game convention.  It
//                 REPLACES the accumulated colour (so it must be the FIRST colour layer, and must
//                 re-sample the sky ramp as its own colour source — the snow `shadowTint` / water
//                 `skyTint` precedent), and it discards u_baseAlbedo, which is desirable here:
//                 the hypsometric terrain colour must not leak into the void.
//   2. starHalo   pixel-locked scatter, larger radius, dim, `colorGate: 2` (the soft dome profile)
//                 — the "bloom" around bright stars.  There is no tonemapping in the lighting
//                 stage, so brightness above 1.0 clips flat; the halo is how a star reads as
//                 BRIGHT rather than just white.
//   3. starCore   pixel-locked scatter, 1-2 px radius, `colorGate: 1` (the crisp mask).  Shares
//                 density/placeJitter/presence/colorJitterSeed with the halo, which (see below)
//                 makes the two layers exactly CONCENTRIC and identically ramp-indexed.
//   4. spikes     pixel-locked cross scatter (shape 2), same shared placement, dim — the 4-point
//                 photographic diffraction spike.
//   (nebula, milkyWay, clusters are DISABLED — see disabledLayerIDs below.)
//
// THE SKY-COLOUR COMPENSATION: in the FULL recipe, the nebula (add) + milkyWay (multiply) +
// clusters (multiply) layers collectively add a faint INDIGO tint to the base sky.  Disabling
// them leaves the plain skyGhost ramp, which the user found "too black / not indigo enough".
// This derived recipe therefore boosts the base sky ramp with a more pronounced indigo cast via
// a `paramOverrides` on `sky_ramp`, so the base texture's background matches what the full
// recipe produced before the layers were removed.  (The exact amount is a best attempt — the
// user said they'll fine-tune it afterward.)
import {MaterialRecipe} from "../@MaterialTypes.js";
import {colorRamp} from "../RecipeUtils.js";
import {STARFIELD_FULL_RECIPE_ID} from "./Recipe_StarfieldFull.js";

export const STARFIELD_RECIPE_ID = "starfield";

export const starfieldRecipe = new MaterialRecipe({
	id: STARFIELD_RECIPE_ID,
	name: "Starfield",
	baseRecipeID: STARFIELD_FULL_RECIPE_ID,

	// Disable the three ambience layers the user found too distracting for the basal texture.
	disabledLayerIDs: ["nebula", "milkyWay", "clusters"],

	// Indigo compensation (see the header): with nebula/milkyWay/clusters gone, the plain sky ramp
	// read too black.  This lifts + blue-shifts the base (same t positions, stronger indigo cast)
	// so the derived texture keeps the background colour the full recipe had.
	paramOverrides: {
		sky_ramp: colorRamp([
			{position: 0  , color_str: `rgb(11,12,29)`}, // deepest void (slightly lifted, more blue)
			{position: 0.5, color_str: `rgb(16,18,39)`}, // dominant night indigo (brighter, more blue)
			{position: 1  , color_str: `rgb(22,24,50)`}, // faint navy lift (brighter, more blue)
		]),
	},
});
